import * as TOKENS from '@shared/tokens.ts';
import { ALL_ACCENT_TOKENS } from '@shared/accents.ts';

export function relativeTime(ms: number | null | undefined): string {
  if (!ms) return '–';
  const diff = Date.now() - ms;
  const sec = Math.round(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? '' : 's'} ago`;
  const month = Math.round(day / 30);
  if (month < 12) return `${month} mo ago`;
  const year = Math.round(month / 12);
  return `${year} yr ago`;
}

// Derived from the real tokens rather than hand-mirrored, so a palette change
// can't leave the studio painting last month's colours.
const COLOUR_HEX: Record<string, string> = Object.fromEntries(
  ALL_ACCENT_TOKENS.map((name) => [name, String(TOKENS[name]).toLowerCase()]),
);

/** The brand default every unknown accent falls back to. Read once here rather
 *  than indexed at each call site, so "the roster has no ORANGE" is a single
 *  loud failure instead of a silent empty string in a style attribute. */
const DEFAULT_HEX = COLOUR_HEX.ORANGE ?? String(TOKENS.ORANGE).toLowerCase();

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * An accent value → the hex to paint. Accepts a brand token name OR a hex,
 * because a section or slide accent may be either since the colour picker
 * landed. The hex passthrough is what stops every custom accent in the app
 * (filmstrip dots, dashboard, library, plan review) rendering orange — this
 * function's fallback is the brand default, and a hex is not a known key.
 */
export function colourHex(value: string | null | undefined): string {
  const v = typeof value === 'string' ? value.trim() : '';
  if (HEX_RE.test(v)) return v.toLowerCase();
  // Deliberately the UNTRIMMED value, as before: only the hex test trims.
  return COLOUR_HEX[typeof value === 'string' ? value : ''] ?? DEFAULT_HEX;
}

/**
 * An identifier → a label. `keyPoints` / `key_points` / `KEY POINTS` all become
 * "Key points". The app labels things with values that arrive from the server as
 * prop names, tool names and enum keys; every one of them is shown to people who
 * do not write code, so nothing reaches the screen still looking like an
 * identifier. Splits camelCase, flattens `_`/`-` to spaces, capitalises once.
 *
 * Returns sentence-cased text; '' for empty input.
 */
export function sentenceCase(raw: unknown): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const words = s
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** How to name an accent in UI PROSE: a token reads as a lowercase word
 *  mid-sentence ("swaps the slide's main accent (orange)"), a custom colour
 *  reads as its hex. For a swatch tooltip or any other standalone label use
 *  `accentName` — labels are capitalised, running prose is not. */
export function accentLabel(value: string | null | undefined): string {
  const v = typeof value === 'string' ? value.trim() : '';
  if (HEX_RE.test(v)) return v.toLowerCase();
  return v ? v.toLowerCase() : '';
}

/** An accent as a standalone LABEL — the swatch tooltip, a meta chip.
 *  Capitalised; a hex passes through unchanged (there is no capital `#aabbcc`). */
export function accentName(value: string | null | undefined): string {
  const v = accentLabel(value);
  if (!v || HEX_RE.test(v)) return v;
  return v.charAt(0).toUpperCase() + v.slice(1);
}

// Tag → URL slug for the public category viewer (/c/<slug>). MUST stay
// byte-identical to the server mirror in server/routes/public.ts (slugifyTag) —
// the Dashboard builds copy-links with this, the server matches decks with it.
export function slugifyTag(tag: unknown): string {
  return String(tag)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// --- deck name → slug -------------------------------------------------------
// The rename/duplicate dialogs show the slug a name will produce as the user
// types. These MUST stay byte-identical to the server mirror in
// server/codecs/deck-naming.ts — the server is the authority, this is the
// preview, and a preview that lies is worse than none.

/** Letters, numbers, spaces and hyphens, checked against the NORMALISED name. */
export const DECK_NAME_RE = /^[A-Za-z0-9 -]+$/;
export const MAX_DECK_NAME = 80;

/** Trim + collapse whitespace, so " My   deck " and "My deck" are one name. */
export function normaliseDeckName(raw: unknown): string {
  return String(raw ?? '')
    .trim()
    .replace(/\s+/g, ' ');
}

function deckKebab(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** The slug a name derives to. `prefix` comes from GET /api/decks (slugPrefix).
 *  A name that already leads with the prefix's word keeps it rather than
 *  doubling up — "RKH AI review" → rkh-ai-review, not rkh-rkh-ai-review. */
export function deckSlugFor(name: unknown, prefix = 'rkh-'): string {
  const body = deckKebab(normaliseDeckName(name));
  if (!body) return '';
  const word = String(prefix || '').replace(/-+$/, '');
  if (!prefix) return body;
  if (word && (body === word || body.startsWith(word + '-'))) return body;
  return prefix + body;
}

/**
 * Slugs no deck may take, and the namespace library-item decks live in.
 *
 * Mirrors the server's `codecs/deck-naming.ts` (RESERVED_SLUGS,
 * LIBRARY_SLUG_PREFIX). It lives here, once, because four call sites used to
 * carry their own hand-copy of the list — which is four places for the rule to
 * drift rather than one.
 */
export const RESERVED_DECK_SLUGS = ['__workbench', 'import', 'trash', 'rkh-library'];

/** The slug namespace of library-item decks (`lib-morgan-sindall`). */
export const LIBRARY_SLUG_PREFIX = 'lib-';

/** True for a library-item deck's slug. */
export function isLibraryItemSlug(slug: unknown): boolean {
  return typeof slug === 'string' && slug.startsWith(LIBRARY_SLUG_PREFIX);
}

/** True when a derived slug is one a deck may not take — reserved outright, or
 *  inside the library's namespace. */
export function isReservedDeckSlug(slug: unknown): boolean {
  return typeof slug === 'string' && (RESERVED_DECK_SLUGS.includes(slug) || isLibraryItemSlug(slug));
}

/** Coerce arbitrary text into a legal deck name: drop disallowed characters,
 *  collapse the gaps, clip to the cap. Used where the app SUGGESTS a name (the
 *  wizard deriving a title from an uploaded brief) — a suggestion the user then
 *  has to hand-fix would be a poor one. Never used on what the user typed. */
export function sanitiseDeckName(raw: unknown): string {
  return normaliseDeckName(String(raw ?? '').replace(/[^A-Za-z0-9 -]+/g, ' '))
    .slice(0, MAX_DECK_NAME)
    .trim();
}

/** Client-side name check, mirroring validateDeckName. Returns an error string
 *  or null. The server re-checks — this exists so the dialog can disable its
 *  button and explain why before a round-trip. */
export function deckNameError(raw: unknown): string | null {
  const name = normaliseDeckName(raw);
  if (!name) return 'Enter a name for the deck.';
  if (name.length > MAX_DECK_NAME) {
    return `Name is too long (${name.length} characters, max ${MAX_DECK_NAME}).`;
  }
  if (!DECK_NAME_RE.test(name))
    return 'Use letters, numbers, spaces and hyphens only, no other punctuation or symbols.';
  if (!deckKebab(name)) return 'Name needs at least one letter or number.';
  return null;
}

// A category path is a deck's organisation label — 1–2 segments joined by "/"
// (e.g. "Clients/Acme"). Parsing + labelling live in categoryTree.js (which
// stays free of styling imports so it is unit-testable); the slug helper below
// stays here beside the other URL/format helpers.

/** Category path → URL slug for the public viewer (/c/<slug>). Each segment is
 *  slugified and re-joined with "/", so "Clients/Acme" → "clients/acme". MUST
 *  stay byte-identical to the server mirror (categoryPathToSlug). */
export function categoryPathToSlug(path: string | null | undefined): string {
  return String(path || '')
    .split('/')
    .map((seg) => slugifyTag(seg))
    .filter(Boolean)
    .join('/');
}

/** A deck's build provenance, as recorded on `DeckSummary` / `studio.json`.
 *  Written out rather than imported so the two nullability spellings there
 *  (`at: string | null` vs `at?: string | null`) both satisfy it. */
export type BuiltBy = {
  engine?: string;
  model?: string;
  at?: string | null;
  effort?: string | null;
  provider?: string | null;
};

// Friendly label for a deck's build provenance badge. The Claude engine shows
// "Claude · Opus" / "Claude · Sonnet"
// (the engine is the product name, the model is what varies underneath); the
// opencode engine shows the model brand it ran ("DeepSeek Pro", "Gemini Flash").
// Returns null when there's nothing to show.
//
// The REASONING RUNG is appended when the build recorded one ("DeepSeek Flash ·
// high"), because it is the difference between two decks the badge would
// otherwise call identical: the same model on one brief gave 58 slides at
// `none` and 35 at `high` (2026-08-04). Absent for engines with no build dial
// and for every deck built before the field existed, and absence stays silent
// rather than guessing a default.
export function formatBuiltBy(builtBy: BuiltBy | null | undefined): string | null {
  const base = builtByBase(builtBy);
  if (!base) return null;
  const parts = [base];
  // WHERE the model came from, when the stamp knows. It sits before the rung
  // because it is the coarser fact, and it is the whole point of a provider
  // comparison: two Pi decks on the same model through different providers are
  // otherwise the same badge. Absent on every deck built before the field
  // existed, and on engines with one provider.
  const provider = formatProviderLabel(builtBy?.provider);
  if (provider) parts.push(provider);
  const rung = builtBy?.effort ? String(builtBy.effort).trim() : '';
  if (rung) parts.push(rung);
  return parts.join(' · ');
}

/**
 * Shorten a stamped provider for a badge.
 *
 * The STAMP is precise (`opencode.ai/zen/go/v1`) because a deck's provenance
 * should survive someone asking exactly which endpoint served it. A card badge
 * has no room for that, so the host's registrable part is what shows —
 * `opencode.ai/zen/go/v1` → `opencode.ai`, while a bare provider id
 * (`openai-codex`) passes through untouched. Shortening here rather than at the
 * stamp keeps the precise value on disk and the short one on screen.
 */
export function formatProviderLabel(provider: string | null | undefined): string {
  const raw = typeof provider === 'string' ? provider.trim() : '';
  if (!raw) return '';
  const host = raw.split('/')[0] || raw;
  return host.replace(/^www\./, '');
}

/**
 * The card-sized stamp: engine · model only. Provider and effort matter when
 * comparing builds (DeckInfoModal shows the full `formatBuiltBy`), but on a
 * deck card they turned one chip into three wrapped lines — there they live
 * in the tooltip instead.
 */
export function formatBuiltByShort(builtBy: BuiltBy | null | undefined): string | null {
  return builtByBase(builtBy);
}

/** The engine/model half of the badge — every branch of the vendor naming. */
function builtByBase(builtBy: BuiltBy | null | undefined): string | null {
  if (!builtBy || !builtBy.engine || !builtBy.model) return null;
  const engine = String(builtBy.engine).toLowerCase();
  const model = String(builtBy.model);
  const m = model.toLowerCase();
  if (engine === 'claude') {
    const name = m.includes('opus')
      ? 'Opus'
      : m.includes('sonnet')
        ? 'Sonnet'
        : m.includes('haiku')
          ? 'Haiku'
          : m.includes('fable')
            ? 'Fable'
            : model;
    return `Claude · ${name}`;
  }
  // Pi and opencode can run the SAME model id against the same endpoint — the
  // engine name is the only thing that tells two such decks apart, so Pi's
  // badge carries it the way Claude's does.
  if (engine === 'codex') return `Codex · ${gatewayModelName(model)}`;
  if (engine === 'pi') return `Pi · ${gatewayModelName(model)}`;
  return gatewayModelName(model);
}

// Gateway model ids look like "opencode-go/deepseek-v4-pro" — drop the
// provider prefix, then prettify the brands we serve.
function gatewayModelName(model: string): string {
  const m = model.toLowerCase();
  const tail = m.includes('/') ? m.slice(m.lastIndexOf('/') + 1) : m;
  const tier = tail.includes('pro') ? ' Pro' : tail.includes('flash') ? ' Flash' : '';
  if (tail.includes('deepseek')) return `DeepSeek${tier}`;
  if (tail.includes('minimax')) return 'MiniMax M3';
  if (tail.includes('kimi')) return tail.includes('k2.7') ? 'Kimi K2.7 Code' : 'Kimi';
  return model.includes('/') ? model.slice(model.lastIndexOf('/') + 1) : model;
}
